/**
 * 代码生成器基类
 * 提供通用的代码生成功能和模板方法
 */

export class BaseCodeGenerator {
  constructor(config = {}) {
    this.config = {
      language: 'unknown',
      indent: '  ', // 默认 2 个空格
      maxLineLength: 80,
      ...config
    };
  }

  /**
   * 生成代码 - 子类必须实现
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 生成的代码
   */
  generate(parsedCurl) {
    throw new Error('子类必须实现 generate 方法');
  }

  /**
   * 生成导入语句
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {Array} 导入语句数组
   */
  generateImports(parsedCurl) {
    return [];
  }

  /**
   * 生成请求代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 请求代码
   */
  generateRequestCode(parsedCurl) {
    const parts = [];

    // URL
    parts.push(this.generateUrlCode(parsedCurl.url));

    // 方法
    if (parsedCurl.method !== 'GET') {
      parts.push(this.generateMethodCode(parsedCurl.method));
    }

    // 请求头
    if (Object.keys(parsedCurl.headers).length > 0) {
      parts.push(this.generateHeadersCode(parsedCurl.headers));
    }

    // 请求体
    if (parsedCurl.data.type !== 'none') {
      parts.push(this.generateDataCode(parsedCurl.data));
    }

    // 认证
    if (parsedCurl.auth.type !== 'none') {
      parts.push(this.generateAuthCode(parsedCurl.auth));
    }

    // Cookie
    if (Object.keys(parsedCurl.cookies).length > 0) {
      parts.push(this.generateCookiesCode(parsedCurl.cookies));
    }

    // 其他选项
    const optionsCode = this.generateOptionsCode(parsedCurl);
    if (optionsCode) {
      parts.push(optionsCode);
    }

    return parts.filter(part => part).join('\n\n');
  }

  /**
   * 生成 URL 代码
   * @param {string} url - URL
   * @returns {string} URL 代码
   */
  generateUrlCode(url) {
    return `url = "${this.escapeString(url)}"`;
  }

  /**
   * 生成 HTTP 方法代码
   * @param {string} method - HTTP 方法
   * @returns {string} 方法代码
   */
  generateMethodCode(method) {
    return `method = "${method}"`;
  }

  /**
   * 生成请求头代码
   * @param {Object} headers - 请求头对象
   * @returns {string} 请求头代码
   */
  generateHeadersCode(headers) {
    const lines = ['headers = {'];

    Object.entries(headers).forEach(([key, value]) => {
      lines.push(`${this.config.indent}"${this.escapeString(key)}": "${this.escapeString(value)}",`);
    });

    // 移除最后一个逗号
    if (lines.length > 1) {
      lines[lines.length - 1] = lines[lines.length - 1].slice(0, -1);
    }

    lines.push('}');

    return lines.join('\n');
  }

  /**
   * 生成请求数据代码
   * @param {Object} data - 数据对象
   * @returns {string} 数据代码
   */
  generateDataCode(data) {
    switch (data.type) {
      case 'json':
        return this.generateJsonDataCode(data.content);
      case 'form':
        return this.generateFormDataCode(data.content);
      case 'raw':
        return this.generateRawDataCode(data.content);
      case 'binary':
        return this.generateBinaryDataCode(data.content);
      default:
        return '';
    }
  }

  /**
   * 生成 JSON 数据代码
   * @param {string} content - JSON 内容
   * @returns {string} JSON 数据代码
   */
  generateJsonDataCode(content) {
    return `data = ${content}`;
  }

  /**
   * 生成表单数据代码
   * @param {string} content - 表单内容
   * @returns {string} 表单数据代码
   */
  generateFormDataCode(content) {
    return `data = "${this.escapeString(content)}"`;
  }

  /**
   * 生成原始数据代码
   * @param {string} content - 原始内容
   * @returns {string} 原始数据代码
   */
  generateRawDataCode(content) {
    return `data = "${this.escapeString(content)}"`;
  }

  /**
   * 生成二进制数据代码
   * @param {string} content - 二进制内容
   * @returns {string} 二进制数据代码
   */
  generateBinaryDataCode(content) {
    return `data = b"${this.escapeString(content)}"`;
  }

  /**
   * 生成认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 认证代码
   */
  generateAuthCode(auth) {
    switch (auth.type) {
      case 'basic':
        return this.generateBasicAuthCode(auth);
      case 'bearer':
        return this.generateBearerAuthCode(auth);
      case 'digest':
        return this.generateDigestAuthCode(auth);
      default:
        return '';
    }
  }

  /**
   * 生成基本认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 基本认证代码
   */
  generateBasicAuthCode(auth) {
    if (auth.username && auth.password !== undefined) {
      return `auth = ("${this.escapeString(auth.username)}", "${this.escapeString(auth.password || '')}")`;
    }
    return '';
  }

  /**
   * 生成 Bearer 认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} Bearer 认证代码
   */
  generateBearerAuthCode(auth) {
    return `headers["Authorization"] = "Bearer ${this.escapeString(auth.token)}"`;
  }

  /**
   * 生成摘要认证代码
   * @param {Object} auth - 认证对象
   * @returns {string} 摘要认证代码
   */
  generateDigestAuthCode(auth) {
    return `auth = HTTPDigestAuth("${this.escapeString(auth.username)}", "${this.escapeString(auth.password || '')}")`;
  }

  /**
   * 生成 Cookie 代码
   * @param {Object} cookies - Cookie 对象
   * @returns {string} Cookie 代码
   */
  generateCookiesCode(cookies) {
    const lines = ['cookies = {'];

    Object.entries(cookies).forEach(([key, value]) => {
      lines.push(`${this.config.indent}"${this.escapeString(key)}": "${this.escapeString(value)}",`);
    });

    // 移除最后一个逗号
    if (lines.length > 1) {
      lines[lines.length - 1] = lines[lines.length - 1].slice(0, -1);
    }

    lines.push('}');

    return lines.join('\n');
  }

  /**
   * 生成选项代码
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {string} 选项代码
   */
  generateOptionsCode(parsedCurl) {
    const options = [];

    if (parsedCurl.insecure) {
      options.push(this.generateInsecureCode());
    }

    if (parsedCurl.followRedirects) {
      options.push(this.generateFollowRedirectsCode());
    }

    if (parsedCurl.connectTimeout > 0) {
      options.push(this.generateTimeoutCode(parsedCurl.connectTimeout));
    }

    if (parsedCurl.proxy) {
      options.push(this.generateProxyCode(parsedCurl.proxy));
    }

    return options.filter(option => option).join('\n');
  }

  /**
   * 生成不安全 SSL 代码
   * @returns {string} 不安全 SSL 代码
   */
  generateInsecureCode() {
    return 'verify = False';
  }

  /**
   * 生成跟随重定向代码
   * @returns {string} 重定向代码
   */
  generateFollowRedirectsCode() {
    return 'allow_redirects = True';
  }

  /**
   * 生成超时代码
   * @param {number} timeout - 超时时间
   * @returns {string} 超时代码
   */
  generateTimeoutCode(timeout) {
    return `timeout = ${timeout}`;
  }

  /**
   * 生成代理代码
   * @param {string} proxy - 代理 URL
   * @returns {string} 代理代码
   */
  generateProxyCode(proxy) {
    return `proxies = {"http": "${this.escapeString(proxy)}", "https": "${this.escapeString(proxy)}"}`;
  }

  /**
   * 转义字符串
   * @param {string} str - 要转义的字符串
   * @param {string} quote - 引号类型
   * @returns {string} 转义后的字符串
   */
  escapeString(str, quote = '"') {
    const escapeMap = {
      '\n': '\\n',
      '\r': '\\r',
      '\t': '\\t',
      '\b': '\\b',
      '\f': '\\f',
      '\v': '\\v'
    };

    // 先处理反斜杠
    str = str.replace(/\\/g, '\\\\');

    // 处理换行符等特殊字符
    for (const [char, escaped] of Object.entries(escapeMap)) {
      str = str.replace(new RegExp(char, 'g'), escaped);
    }

    // 处理引号
    if (quote === '"') {
      str = str.replace(/"/g, '\\"');
    } else if (quote === "'") {
      str = str.replace(/'/g, "\\'");
    }

    return str;
  }

  /**
   * 格式化代码
   * @param {string} code - 原始代码
   * @returns {string} 格式化后的代码
   */
  formatCode(code) {
    if (!code) return '';

    return code
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n');
  }

  /**
   * 缩进代码
   * @param {string} code - 代码
   * @param {number} level - 缩进级别
   * @returns {string} 缩进后的代码
   */
  indentCode(code, level = 1) {
    const indent = this.config.indent.repeat(level);
    return code
      .split('\n')
      .map(line => (line.trim() ? indent + line : line))
      .join('\n');
  }

  /**
   * 检查是否需要特殊处理
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {Object} 检查结果
   */
  needsSpecialHandling(parsedCurl) {
    return {
      hasAuth: parsedCurl.auth.type !== 'none',
      hasData: parsedCurl.data.type !== 'none',
      hasCookies: Object.keys(parsedCurl.cookies).length > 0,
      hasCustomHeaders: Object.keys(parsedCurl.headers).length > 0,
      hasOptions: parsedCurl.insecure || parsedCurl.followRedirects ||
                  parsedCurl.connectTimeout > 0 || parsedCurl.proxy
    };
  }

  /**
   * 生成注释
   * @param {string} text - 注释文本
   * @returns {string} 注释代码
   */
  generateComment(text) {
    return `# ${text}`;
  }

  /**
   * 生成多行注释
   * @param {Array} lines - 注释行数组
   * @returns {string} 多行注释代码
   */
  generateMultilineComment(lines) {
    const commentLines = lines.map(line => `# ${line}`);
    return commentLines.join('\n');
  }

  /**
   * 获取语言特定的配置
   * @returns {Object} 语言配置
   */
  getLanguageConfig() {
    return this.config;
  }

  /**
   * 验证解析结果
   * @param {Object} parsedCurl - 解析后的 curl 对象
   * @returns {boolean} 是否有效
   */
  validateParsedCurl(parsedCurl) {
    if (!parsedCurl.url) {
      throw new Error('缺少 URL');
    }

    if (!parsedCurl.method) {
      throw new Error('缺少 HTTP 方法');
    }

    return true;
  }
}